import { useMemo, useState, type ReactNode } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useClasses,
  useDepartments,
  useHierarchyLevel,
  useSubclasses,
} from '@/hooks/useHierarchy'
import { useSeasons } from '@/hooks/useSeasons'
import { formatDistributionCenterName } from '@/utils/formatters'
import {
  buildCostShiftMatrix,
  DC_OPTIONS,
  EVENT_OPTIONS,
  SNAPSHOT_DATE_OPTIONS,
  VIEW_BY_OPTIONS,
  type CostShiftFilterState,
} from './costShiftGridMockData'
import { CostShiftGridTable } from './CostShiftGridTable'

const ALL = '__all__'

function FilterField({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  )
}

function SimpleSelect({
  value,
  onValueChange,
  options,
  loading,
  formatOption = option => option,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: string[]
  loading?: boolean
  formatOption?: (option: string) => string
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o} value={o}>
            {formatOption(o)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

interface IdOption {
  id: string
  label: string
}

function IdSelect({
  value,
  onValueChange,
  options,
  loading,
  disabled,
}: {
  value: string | null
  onValueChange: (v: string | null) => void
  options: IdOption[]
  loading?: boolean
  disabled?: boolean
}) {
  return (
    <Select
      value={value ?? ALL}
      onValueChange={v => onValueChange(v === ALL ? null : v)}
      disabled={disabled}
    >
      <SelectTrigger className="w-full bg-white">
        <SelectValue placeholder={loading ? 'Loading...' : 'All'} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>All</SelectItem>
        {options.map(o => (
          <SelectItem key={o.id} value={o.id}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

const DESCRIPTION =
  'Summarizes how projected PO "anticipated arrival" dollars are shifting between months over time, based on DC receipt timing. It helps you quickly see whether inventory value is being pushed out delayed or pulled in accelerated compared to the original Arrival Date.'

interface Props {
  onDrillCell?: (
    from: string,
    to: string,
    filters: CostShiftFilterState
  ) => void
}

export function CostShiftGridMockup({ onDrillCell }: Props) {
  const [snapshotDate, setSnapshotDate] = useState<string>('Latest')
  const [season, setSeason] = useState<string | null>(null)
  const [event, setEvent] = useState<string | null>(null)
  const [dc, setDc] = useState<string | null>(null)
  const [viewBy, setViewBy] = useState<string>('Inventory dollars')
  const [divisionId, setDivisionId] = useState<string | null>(null)
  const [departmentId, setDepartmentId] = useState<string | null>(null)
  const [classId, setClassId] = useState<string | null>(null)
  const [subclassId, setSubclassId] = useState<string | null>(null)

  const { data: seasons, isLoading: seasonsLoading } = useSeasons()
  const { data: divisions, isLoading: divisionsLoading } =
    useHierarchyLevel('DIVISION')
  const { data: departmentsScoped, isLoading: departmentsScopedLoading } =
    useHierarchyLevel('DEPARTMENT', divisionId)
  const { data: departmentsAll, isLoading: departmentsAllLoading } =
    useDepartments()
  const { data: classes, isLoading: classesLoading } = useClasses(departmentId)
  const { data: subclasses, isLoading: subclassesLoading } =
    useSubclasses(classId)

  const departments = divisionId ? departmentsScoped : departmentsAll
  const departmentsLoading = divisionId
    ? departmentsScopedLoading
    : departmentsAllLoading

  const seasonOptions = (seasons ?? []).map(s => s.name)
  const divisionOptions: IdOption[] = (divisions ?? []).map(d => ({
    id: d.id,
    label: `${d.code} - ${d.name}`,
  }))
  const departmentOptions: IdOption[] = (departments ?? []).map(d => ({
    id: d.id,
    label: `${d.code} - ${d.name}`,
  }))
  const classOptions: IdOption[] = (classes ?? []).map(c => ({
    id: c.id,
    label: `${c.code} - ${c.name}`,
  }))
  const subclassOptions: IdOption[] = (subclasses ?? []).map(s => ({
    id: s.id,
    label: `${s.code} - ${s.name}`,
  }))

  const filters = useMemo<CostShiftFilterState>(
    () => ({
      snapshotDate,
      season,
      event,
      dc,
      viewBy,
      divisionId,
      departmentId,
      classId,
      subclassId,
    }),
    [
      snapshotDate,
      season,
      event,
      dc,
      viewBy,
      divisionId,
      departmentId,
      classId,
      subclassId,
    ]
  )

  const matrix = useMemo(() => buildCostShiftMatrix(filters), [filters])

  const handleDivisionChange = (v: string | null) => {
    setDivisionId(v)
    setDepartmentId(null)
    setClassId(null)
    setSubclassId(null)
  }

  const handleDepartmentChange = (v: string | null) => {
    setDepartmentId(v)
    setClassId(null)
    setSubclassId(null)
  }

  const handleClassChange = (v: string | null) => {
    setClassId(v)
    setSubclassId(null)
  }

  return (
    <div className="flex flex-col gap-4 bg-[#fafafa] p-6">
      {/* Top strip */}
      <p className="max-w-2xl text-[11px] leading-tight text-muted-foreground">
        {DESCRIPTION}
      </p>

      {/* Filter grid — 2 rows of 5, offset to align with the screenshot */}
      <div className="grid grid-cols-5 gap-4">
        <div />
        <FilterField label="Snapshot Date">
          <Select value={snapshotDate} onValueChange={setSnapshotDate}>
            <SelectTrigger className="w-full bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SNAPSHOT_DATE_OPTIONS.map(o => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Season">
          <SimpleSelect
            value={season}
            onValueChange={setSeason}
            options={seasonOptions}
            loading={seasonsLoading}
          />
        </FilterField>
        <FilterField label="Event">
          <SimpleSelect
            value={event}
            onValueChange={setEvent}
            options={EVENT_OPTIONS}
          />
        </FilterField>
        <FilterField label="DC">
          <SimpleSelect
            value={dc}
            onValueChange={setDc}
            options={DC_OPTIONS}
            formatOption={formatDistributionCenterName}
          />
        </FilterField>

        <FilterField label="View By">
          <Select value={viewBy} onValueChange={setViewBy}>
            <SelectTrigger className="w-full bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {VIEW_BY_OPTIONS.map(o => (
                <SelectItem key={o} value={o}>
                  {o}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FilterField>
        <FilterField label="Division">
          <IdSelect
            value={divisionId}
            onValueChange={handleDivisionChange}
            options={divisionOptions}
            loading={divisionsLoading}
          />
        </FilterField>
        <FilterField label="Department">
          <IdSelect
            value={departmentId}
            onValueChange={handleDepartmentChange}
            options={departmentOptions}
            loading={departmentsLoading}
          />
        </FilterField>
        <FilterField label="Class">
          <IdSelect
            value={classId}
            onValueChange={handleClassChange}
            options={classOptions}
            loading={classesLoading}
            disabled={!departmentId}
          />
        </FilterField>
        <FilterField label="SubClass">
          <IdSelect
            value={subclassId}
            onValueChange={setSubclassId}
            options={subclassOptions}
            loading={subclassesLoading}
            disabled={!classId}
          />
        </FilterField>
      </div>

      <CostShiftGridTable
        matrix={matrix}
        viewBy={viewBy}
        onCellClick={
          onDrillCell ? (from, to) => onDrillCell(from, to, filters) : undefined
        }
      />
    </div>
  )
}
